import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { isSaleOpen, soonestLotEndMs } from '../utils/saleState';
import { serverNow } from '../utils/serverClock';
import { useAuctionClock } from './useAuctionClock';

/**
 * The sales that are OPEN right now (isSaleOpen — the same definition PublicAuctionList uses),
 * soonest-closing first. Live: a soft-close extension or a lot closing re-derives the list on the
 * shared adaptive clock, exactly as the sale list does.
 *
 *   status 'loading'  first snapshot not in yet
 *   status 'error'    Firestore unreachable — callers show the no-sale layout, never a blank page
 */
export type OpenSale = Auction & { _soonestEndMs: number | null };

export function useOpenSales(): { status: 'loading' | 'ready' | 'error'; open: OpenSale[] } {
  const [sales, setSales] = useState<Auction[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'auctions'),
      (snap) => {
        setSales(snap.docs.map((d) => {
          const data = d.data() as any;
          return { ...data, id: d.id, lots: data.lots || [] } as Auction;
        }));
        setStatus('ready');
      },
      (err) => { console.error('Home: sales snapshot error', err); setStatus('error'); },
    );
    return () => unsub();
  }, []);

  const soonestEnd = useMemo(() => {
    const nowRef = serverNow();
    let soonest: number | null = null;
    for (const s of sales) {
      const t = soonestLotEndMs(s.lots || [], nowRef);
      if (t !== null && (soonest === null || t < soonest)) soonest = t;
    }
    return soonest;
  }, [sales]);
  const nowMs = useAuctionClock(soonestEnd);

  const open = useMemo(() => {
    const now = new Date(nowMs);
    return sales
      .filter((s) => isSaleOpen(s, now))
      .map((s) => ({ ...s, _soonestEndMs: soonestLotEndMs(s.lots || [], nowMs) }))
      .sort((a, b) => (a._soonestEndMs ?? Infinity) - (b._soonestEndMs ?? Infinity) || (a.title || '').localeCompare(b.title || '') || a.id.localeCompare(b.id));
  }, [sales, nowMs]);

  return { status, open };
}
